import { z } from 'zod';
import { IsoDateTime, UuidString } from '../../primitives.js';
import { ProjectKind } from './project.js';
import { TaskPriority } from './task.js';
import { TemplateSnapshot } from './template-snapshot.js';

/**
 * Blueprint — шаблон проекта/набора задач (BE-S15-1).
 *
 * Materializer (engines-pure/blueprint-materializer.ts) превращает blueprint
 * в Project + Task[] детерминированно: uuid/clock инжектятся снаружи
 * (Temporal-safe, CI inv 6).
 */

export const BlueprintTaskTemplate = z.object({
  /** Локальный ключ внутри blueprint (для зависимостей между шаблонами). */
  key: z.string().min(1).max(64),
  title: z.string().min(1).max(500),
  priority: TaskPriority.default('none'),
  /** Смещение в днях от старта материализации. */
  start_offset_days: z.number().int().min(0).default(0),
  duration_days: z.number().int().min(1).default(1),
  /** role_key для role-assignee (claim-pattern). */
  assignee_role: z.string().max(64).nullable().default(null),
  /** Ключи задач-шаблонов, которые блокируют эту (blocked_by). */
  depends_on: z.array(z.string()).default([]),
  custom_fields: z.record(z.string(), z.unknown()).default({}),
});
export type BlueprintTaskTemplate = z.infer<typeof BlueprintTaskTemplate>;

export const Blueprint = z
  .object({
    id: UuidString,
    entity_id: UuidString,
    name: z.string().min(1).max(200),
    kind: ProjectKind,
    version: z.number().int().positive(),
    /** Активная версия — единственная материализуемая. */
    is_active: z.boolean(),
    tasks: z.array(BlueprintTaskTemplate),
    /** v2-снапшот проекта (project-templates §2). null → v1-blueprint (плоский). */
    snapshot: TemplateSnapshot.nullable().default(null),
    /** Описание шаблона (v2). */
    description: z.string().nullable().default(null),
    created_by_user_id: UuidString,
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
  })

  .refine(
    (b) => {
      const keys = new Set(b.tasks.map((t) => t.key));
      return keys.size === b.tasks.length;
    },
    { message: 'Ключи task-шаблонов должны быть уникальны', path: ['tasks'] },
  )
  .refine(
    (b) => {
      const keys = new Set(b.tasks.map((t) => t.key));
      return b.tasks.every((t) => t.depends_on.every((d) => keys.has(d)));
    },
    { message: 'depends_on ссылается на несуществующий ключ', path: ['tasks'] },
  );
export type Blueprint = z.infer<typeof Blueprint>;

/** Правило повторения (S16 recurring) — RRULE-строка + idempotency окно. */
export const RecurringRule = z.object({
  id: UuidString,
  entity_id: UuidString,
  project_id: UuidString,
  /** RFC 5545 RRULE (FREQ=MONTHLY;BYMONTHDAY=1 и т.п.). */
  rrule: z.string().min(1).max(500),
  task_template: BlueprintTaskTemplate,
  is_active: z.boolean(),
  created_at: IsoDateTime,
});
export type RecurringRule = z.infer<typeof RecurringRule>;
